// Admin → Flow → Session Access → "Assign customers" table: search + sort (2026-10-04).
// Works only on what the page has ALREADY loaded (no Firestore reads), so it is free and
// instant. Pure functions (no React, no Firestore) so the rules can be tested on their own.
//   • Search matches anything the row shows — customer, WhatsApp, both dates, Gmail,
//     status, session — plus the customer's name and the Gmail's label / address.
//   • WhatsApp compares digits only and treats 0… / 60… / +60… as the same number
//     (shared rule: utils/nomborTelefon.js terasNombor).
//   • Several words: rows where ONE cell holds the whole phrase win ("15 oct"); only when
//     no row does are the words matched separately across cells ("flow_04 offline").
//   • Sort: empty cells always go last, whichever direction.

import { terasNombor } from './nomborTelefon';

const kecil = value => String(value ?? '').toLowerCase().trim();

// Status sorts by urgency, not alphabet: Problem → Needs attention → Online → Offline / not tried.
const TURUTAN_NADA = { bad: 0, warn: 1, ok: 2, idle: 3 };
// The legend's words, so typing "problem" or "online" finds the matching dots.
const ALIAS_NADA = { ok: 'online', warn: 'needs attention', bad: 'problem' };

// One table row → `medan` (lower-case text of every cell, for search) + `kunci` (sort values).
// st = statusPelanggan() result for that customer (utils/flowStatusPelanggan.js).
export function barisCarian({ pelanggan, lain = [], telefon, tamatSub, teksTamatSub, gmail, gmailLain = [], aksesHingga, teksAkses, st }) {
    const sesi = st?.version ? `v${st.version} ${st.outdated ? 'outdated' : 'latest'}` : '';
    const status = [st?.label, st?.when, ALIAS_NADA[st?.tone]].filter(Boolean).join(' ');
    return {
        medan: [pelanggan, ...lain, telefon, teksTamatSub, gmail, ...gmailLain, teksAkses, status, sesi].map(kecil).filter(Boolean),
        digit: String(telefon || '').replace(/\D/g, ''),
        teras: terasNombor(telefon),
        kunci: {
            customer: kecil(pelanggan),
            whatsapp: terasNombor(telefon),
            subExpiry: Number.isFinite(tamatSub) ? tamatSub : null,
            gmail: kecil(gmail),
            accessUntil: aksesHingga ?? null,          // Infinity = "no end" (sorts as the latest date)
            status: st ? `${TURUTAN_NADA[st.tone] ?? 9} ${kecil(st.label)}` : '',
            session: st?.version ?? null,
        },
    };
}

// A typed number vs the row's WhatsApp, whatever the formatting or prefix.
const padanTelefon = (baris, teks) => {
    if (!baris.digit || !/^[\d\s+()-]+$/.test(teks)) return false;
    const digit = teks.replace(/\D/g, '');
    if (digit.length < 2) return false;
    if (baris.digit.includes(digit)) return true;
    const teras = terasNombor(digit);
    // Typed WITH a prefix (0… / 60…) = typed from the start of the number.
    return teras !== digit && !!teras && baris.teras.startsWith(teras);
};
const padan = (baris, teks) => baris.medan.some(m => m.includes(teks)) || padanTelefon(baris, teks);

export function tapisBaris(baris, carian) {
    const frasa = kecil(carian).replace(/\s+/g, ' ');
    if (!frasa) return baris;
    const tepat = baris.filter(b => padan(b, frasa));
    const kata = frasa.split(' ');
    if (tepat.length || kata.length < 2) return tepat;
    return baris.filter(b => kata.every(k => padan(b, k)));
}

const kosong = value => value === null || value === undefined || value === '';
export function susunBaris(baris, lajur, arah) {
    if (!lajur || !arah) return baris;
    const tanda = arah === 'desc' ? -1 : 1;
    return baris.map((b, i) => [b, i]).sort(([x, i], [y, j]) => {
        const p = x.kunci[lajur], q = y.kunci[lajur];
        if (kosong(p) || kosong(q)) return kosong(p) === kosong(q) ? i - j : (kosong(p) ? 1 : -1);
        const beza = typeof p === 'number' && typeof q === 'number'
            ? (p < q ? -1 : p > q ? 1 : 0)
            : String(p).localeCompare(String(q), 'en', { numeric: true });
        return beza ? beza * tanda : i - j;
    }).map(([b]) => b);
}

// Clicking a column header: A→Z, then Z→A, then back to the original order.
export const susunSeterusnya = (semasa, lajur) => semasa.lajur !== lajur ? { lajur, arah: 'asc' }
    : semasa.arah === 'asc' ? { lajur, arah: 'desc' } : { lajur: null, arah: null };
